import { BrandRequestDao, BrandResearchDao } from '@rock/db-admin';
import type { BrandRequest, BrandRequestId, BrandRequestStatus, BrandResearch } from '@rock/types';
import { shallowEqual } from '@rock/util';
import { atom } from 'jotai';
import { atomFamily, atomWithRefresh } from 'jotai/utils';
import logger from '@/util/logger';

const log = logger('brandRequests.state');
const brandRequestDao = new BrandRequestDao();
const brandResearchDao = new BrandResearchDao();

/** The queue page caps its list; a queue this size means something upstream is wrong. */
export const BRAND_REQUESTS_LIMIT = 200;

/** The brand requests in one status, most recently seen first. */
export const brandRequestsState = atomFamily((status: BrandRequestStatus) =>
    brandRequestDao.requestsAtom(status, BRAND_REQUESTS_LIMIT),
);

export const brandRequestState = atomFamily(
    (requestId: BrandRequestId | undefined) => brandRequestDao.requestAtom(requestId),
    shallowEqual,
);

/** A request's AI research runs, newest first; live, so a run in progress shows when it lands. */
export const brandResearchState = atomFamily((requestId: BrandRequestId | undefined) =>
    brandResearchDao.researchAtom(requestId),
);

/** A request's latest research run, live: the queue's Researched column. */
const latestBrandResearchState = atomFamily((requestId: BrandRequestId) => brandResearchDao.researchAtom(requestId, 1));

/** Each request's latest research run, from one `joinAliasKeys`-joined key of request ids. */
export const latestBrandResearchLookupState = atomFamily((joinedIds: string) =>
    atom(async (get): Promise<Map<BrandRequestId, BrandResearch>> => {
        const ids = (joinedIds ? joinedIds.split('\n') : []) as BrandRequestId[];
        const runs = await Promise.all(ids.map((id) => get(latestBrandResearchState(id))));
        return new Map(
            ids.flatMap((id, index) => {
                const latest = runs[index]?.[0];
                return latest ? [[id, latest] as const] : [];
            }),
        );
    }),
);

/** Several requests by id, from one `joinAliasKeys`-joined key (sorted, so one atom per set of names). */
export const brandRequestLookupState = atomFamily((joinedIds: string) =>
    atom(async (get): Promise<Map<BrandRequestId, BrandRequest | undefined>> => {
        const ids = (joinedIds ? joinedIds.split('\n') : []) as BrandRequestId[];
        const requests = await Promise.all(ids.map((id) => get(brandRequestState(id))));
        return new Map(ids.map((id, index) => [id, requests[index]]));
    }),
);

/**
 * How many names are waiting on a human — the nav badge and the queue header. Refreshable so each reader
 * marks it stale on unmount; undefined when the count fails, which readers render as nothing.
 */
export const pendingBrandRequestCountState = atomWithRefresh(async (): Promise<number | undefined> => {
    try {
        return await brandRequestDao.countPending();
    } catch (err) {
        log.error('Failed to count pending brand requests', { err });
        return undefined;
    }
});
